import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { analog_base_url, customer_account_base_url } from "../../../../../utils/urls";
import { toastify } from '../../../../../utils/toast';
import Layout from '../../../component/layout';
import { getItem, clearStorage } from "../../../../../utils/localStorage";
import { useFacilityStore } from '../../../../../app/z-store/store';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

function ViewAnalogWaterMeter() {
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);
    const navigate = useNavigate();
    const location = useLocation();
    const { meterId: paramMeterId } = useParams();
    const stateMeterId = location.state?.meterId;
    const meterId = paramMeterId || stateMeterId;

    const [meterDetails, setMeterDetails] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [readings, setReadings] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState('');
    const [activeSection, setActiveSection] = useState('meter-info');
    const [units, setUnits] = useState([]);

    const [customerAccounts, setCustomerAccounts] = useState([]);

    const [showAssignCustomerModal, setShowAssignCustomerModal] = useState(false);
    const [availableCustomers, setAvailableCustomers] = useState([]);
    const [selectedCustomerForAssignment, setSelectedCustomerForAssignment] = useState('');
    const [monthlyConsumption, setMonthlyConsumption] = useState([]);
    const [invoices, setInvoices] = useState([]);

    const [fullname, setFullname] = useState('');
    const [role, setRole] = useState('');

    // Modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddReadingModal, setShowAddReadingModal] = useState(false);
    const [editForm, setEditForm] = useState({
        meterNumber: '',
        accountNumber: ''
    });
    const [newReading, setNewReading] = useState('');
    const [showEditAccountModal, setShowEditAccountModal] = useState(false);
    const [selectedAccountForEdit, setSelectedAccountForEdit] = useState(null);
    const [editAccountForm, setEditAccountForm] = useState({
        customerName: '',
        phoneNumber: '',
        email: ''
    });

    useEffect(() => {
        if (meterId) {
            const today = new Date().toISOString().split('T')[0]; // "YYYY-MM-DD"
            setSelectedDate(today);
            fetchMeterDetails();
            fetchDailyReadings();
            fetchMonthlyConsumption();
            fetchUnits();
        } else {
            toastify('Meter ID not provided', 'error');
            navigate('/facility/utility_management/meter_management/water_meters');
        }
    }, [meterId]);

    useEffect(() => {
        const getCurrentUser = async () => {
            try {
                const currentUserValue = await getItem("APPUSER");
                let currentUser;
                // Check if the value needs to be parsed
                if (typeof currentUserValue === "string") {
                    try {
                        currentUser = JSON.parse(currentUserValue);
                    } catch (err) {
                        console.error("Error parsing APPUSER:", err);
                    }
                } else {
                    currentUser = currentUserValue; // Assume it's already an object
                }

                // Safely access and set user details
                if (currentUser?.user) {
                    const fullName = currentUser.user.fullName;
                    const role = currentUser.user.role;
                    setFullname(fullName);
                    setRole(role);
                } else {
                    console.error("Invalid user structure:", currentUser);
                }
            } catch (err) {
                console.log(err.message);
            }
        };

        getCurrentUser();
    }, []);

    useEffect(() => {
        // Populate edit form when meterDetails are fetched
        if (meterDetails) {
            setEditForm({
                meterNumber: meterDetails.meterNumber || '',
                accountNumber: meterDetails.accountNumber || '',
                unitId: meterDetails.unitId || ''
            });
        }
    }, [meterDetails]);

    // Fetch available customers to assign
    const fetchAvailableCustomers = async () => {
        try {
            const response = await makeRequest2(
                `/api/app/customer_management/get_customers/${selectedFacilityId}`,
                'GET'
            );

            if (response.success) {
                const customers = response.data?.customers || response.data || [];

                // Filter out customers who are not already assigned to this meter
                const unassignedCustomers = customers.filter(customer =>
                    !customerAccounts.some(account => account.customerId === customer._id)
                );

                setAvailableCustomers(unassignedCustomers);
                setShowAssignCustomerModal(true);
            } else {
                toastify('Failed to fetch customers', 'error');
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            toastify('Error fetching customers', 'error');
        }
    };

    // Fetch monthly consumption history for this meter
    const fetchMonthlyConsumption = async () => {
        if (!meterId) return;
        try {
            const response = await makeRequest2(
                `${analog_base_url}/get_monthly_consumption/${meterId}`,
                'GET'
            );

            if (response.success && response.data.data) {
                const formattedConsumption = response.data.data.map((item, index) => ({
                    yearMonth: item.yearMonth,
                    consumption: item.consumption ? item.consumption.toFixed(2) : '0.00',
                    initialReading: item.initialReading ? item.initialReading.toFixed(2) : '0.00',
                    finalReading: item.finalReading ? item.finalReading.toFixed(2) : '0.00'
                }));
                setMonthlyConsumption(formattedConsumption);
            } else {
                setMonthlyConsumption([]);
            }
        } catch (error) {
            console.error('Error fetching monthly consumption:', error);
            setMonthlyConsumption([]);
        }
    };

    // 2. Add fetchInvoices function (add this with other fetch functions)
    const fetchInvoices = async () => {
        if (!meterDetails?.meterNumber || !meterDetails?.customerId) return;

        try {
            const response = await makeRequest2(
                `/api/app/water-invoices/get_meter_invoices/${meterDetails.meterNumber}/${meterDetails.customerId}`,
                'GET'
            );

            if (response.success && response.data.data?.invoices) {
                setInvoices(response.data.data.invoices);
            } else {
                setInvoices([]);
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            setInvoices([]);
        }
    };

    // Fetch daily readings for this meter
    const fetchDailyReadings = async () => {
        if (!meterId) return;
        try {
            const response = await makeRequest2(
                `${analog_base_url}/get_daily_readings/${meterId}`,
                'GET'
            );

            if (response.success && response.data.data) {
                const formattedReadings = response.data.data.map((reading, index) => ({
                    date: new Date(reading.date).toLocaleDateString(),
                    reading: reading.reading ? reading.reading.toFixed(2) : '0.00',
                    status: reading.status || 'N/A'
                }));
                setReadings(formattedReadings);
            } else {
                setReadings([]);
            }
        } catch (error) {
            console.error('Error fetching daily readings:', error);
            setReadings([]);
        }
    };

    // Handle view invoice action
    const handleViewInvoice = (invoice) => {
        navigate("/facility/utility_management/invoice_management/invoice_page", {
            state: {
                invoiceId: invoice._id,
                invoiceNumber: invoice.invoiceNumber
            }
        });
    };

    // Export invoices to CSV
    const exportInvoicesCSV = () => {
        if (invoices.length === 0) {
            toastify('No invoices to export', 'warning');
            return;
        }

        try {
            const header = [
                'Inv Num',
                'Date Issued',
                'Period',
                'Usage (m³)',
                'Amount (KES)',
                'Paid (KES)',
                'Balance (KES)',
                'Status',
                'Due Date',
                'SMS Sent',
                'Email Sent'
            ];

            const data = invoices.map(invoice => {
                const balance = (invoice.charges?.totalMonthlyBill || 0) - (invoice.amountPaid || 0);
                const notifications = invoice.notificationsSent || {};

                return [
                    invoice.invoiceNumber || 'N/A',
                    invoice.dateIssued ? new Date(invoice.dateIssued).toLocaleDateString() : 'N/A',
                    invoice.yearMonth || 'N/A',
                    invoice.meterReadings?.usage || '0',
                    (invoice.charges?.totalMonthlyBill || 0).toFixed(2),
                    (invoice.amountPaid || 0).toFixed(2),
                    balance.toFixed(2),
                    invoice.status || 'N/A',
                    invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A',
                    notifications.sms ? 'Yes' : 'No',
                    notifications.email ? 'Yes' : 'No'
                ];
            });

            const csvContent = [
                header.join(','),
                ...data.map(row =>
                    row.map(cell =>
                        typeof cell === 'string' && cell.includes(',')
                            ? `"${cell}"`
                            : cell
                    ).join(',')
                )
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `Water_Invoices_${meterDetails?.meterNumber}_${new Date().toISOString().slice(0, 10)}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toastify('Invoices exported to CSV successfully', 'success');
        } catch (error) {
            console.error('Error exporting invoices:', error);
            toastify('Error exporting invoices', 'error');
        }
    };

    // Enhanced notification status template (if you want a text version too)
    const getNotificationStatus = (invoice) => {
        const notifications = invoice.notificationsSent || {};
        const smsStatus = notifications.sms || false;
        const emailStatus = notifications.email || false;

        if (smsStatus && emailStatus) {
            return 'SMS & Email';
        } else if (emailStatus) {
            return 'Email';
        } else if (smsStatus) {
            return 'SMS';
        } else {
            return 'None';
        }
    };

    // Fetch meter details using the meterId
    const fetchMeterDetails = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${analog_base_url}/details/${selectedFacilityId}/${meterId}`,
                'GET'
            );

            if (response.success) {
                const meterData = response.data.meter;
                setMeterDetails(meterData);

                if (meterData.meterNumber) {
                    fetchCustomerAccountsByMeterNumber(meterData.meterNumber);
                    // Fetch invoices after meter data is loaded
                    if (meterData.customerId) {
                        fetchInvoices();
                    }
                }

                fetchTransactions(meterData.customerId);
            } else {
                toastify('Failed to fetch meter details', 'error');
            }
        } catch (error) {
            console.error('Error fetching meter details:', error);
            toastify('Error fetching meter details', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Updated function to fetch customer accounts by meter number
    const fetchCustomerAccountsByMeterNumber = async (meterNumber) => {
        if (!meterNumber) return;
        try {
            const response = await makeRequest2(
                `/api/app/customer-account/get_accounts_by_meter/${selectedFacilityId}/${meterNumber}`,
                'GET'
            );

            // Check for response.data.data which is where the accounts array is
            if (response.data?.data && response.data.data.length > 0) {
                console.log("Customer accounts received:", response.data.data);

                // Sort accounts to ensure Active accounts appear first
                const sortedAccounts = [...response.data.data].sort((a, b) => {
                    // Active accounts first
                    if (a.status === 'Active' && b.status !== 'Active') return -1;
                    if (a.status !== 'Active' && b.status === 'Active') return 1;

                    // Then sort by most recently updated
                    return new Date(b.updatedAt || b.created_on) - new Date(a.updatedAt || a.created_on);
                });

                setCustomerAccounts(sortedAccounts);

                // Set a single customer for backward compatibility
                const activeAccount = sortedAccounts.find(acc => acc.status === 'Active') || sortedAccounts[0];
                setCustomer({
                    customerId: activeAccount.customerId || 'N/A',
                    name: activeAccount.customerName || 'N/A',
                    contactNumber: activeAccount.phoneNumber || 'N/A',
                    accountType: activeAccount.payment_type || 'N/A',
                    status: activeAccount.status || 'N/A',
                    updatedAt: activeAccount.updatedAt || activeAccount.created_on || 'N/A'
                });
            } else {
                console.warn('No customer accounts found for this meter:', response);
                setCustomerAccounts([]);
                setCustomer(null);
            }
        } catch (error) {
            console.error('Error fetching customer accounts:', error);
            setCustomerAccounts([]);
            setCustomer(null);
        }
    };
    const fetchUnits = async () => {
        try {
            const response = await makeRequest2(
                `/api/app/unit_management/get_facility_units/${selectedFacilityId}`,
                'GET'
            );
            if (response.success) {
                const unitsData = response.data?.units || response.data || [];
                setUnits(unitsData);
            }
        } catch (error) {
            console.error('Error fetching units:', error);
        }
    };

    // Fetch transactions related to this meter
    const fetchTransactions = async (customerId) => {
        if (!customerId) return;
        try {
            // Implement transactions fetch if endpoint is available
            setTransactions([]);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            setTransactions([]);
        }
    };

    const handleDateChange = (value) => {
        setSelectedDate(value);
    };

    const handleFilter = () => {
        fetchDailyReadings();
    };

    // Handle submission of edited meter details
    const handleEditSubmit = async () => {
        try {
            setLoading(true);
            const updateData = {
                ...editForm,
                unitId: editForm.unitId // Make sure unitId is included
            };
            const response = await makeRequest2(
                `${analog_base_url}/${selectedFacilityId}/${meterId}`,
                'PUT',
                updateData
            );
            if (response.success) {
                toastify('Meter updated successfully', 'success');
                setShowEditModal(false);
                fetchMeterDetails();
            } else {
                toastify('Failed to update meter', 'error');
            }
        } catch (error) {
            console.error('Error updating meter:', error);
            toastify('Error updating meter', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle submission of a new reading
    const handleNewReadingSubmit = async () => {
        try {
            setLoading(true);
            const updateData = {
                unitName: meterDetails?.UnitInfo
                    ? (meterDetails.UnitInfo.name || meterDetails.UnitInfo.unitNumber)
                    : 'N/A',
                currentReading: parseFloat(newReading),
                readBy: fullname || 'Property Manager'
            };
            const response = await makeRequest2(
                `${analog_base_url}/add_new_readings/${meterId}`,
                'PUT',
                updateData
            );
            if (response.success) {
                toastify('New reading recorded successfully', 'success');
                setShowAddReadingModal(false);
                setNewReading('');
                fetchMeterDetails();
            } else {
                toastify('Failed to record new reading', 'error');
            }
        } catch (error) {
            console.error('Error recording new reading:', error);
            toastify('Error recording new reading', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Toggle the meter status between open and closed
    const handleToggleMeterStatus = async () => {
        try {
            setLoading(true);
            const newStatus = meterDetails?.status === 'closed' ? 'opened' : 'closed';
            const updateData = { status: newStatus };
            const response = await makeRequest2(
                `${analog_base_url}/${selectedFacilityId}/${meterId}`,
                'PUT',
                updateData
            );
            if (response.success) {
                toastify(`Meter status updated to ${newStatus}`, 'success');
                fetchMeterDetails();
            } else {
                toastify('Failed to update meter status', 'error');
            }
        } catch (error) {
            console.error('Error toggling meter status:', error);
            toastify('Error updating meter status', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Add this new function to your ViewAnalogWaterMeter component

    const handleGenerateBill = async () => {
        try {
            if (!meterDetails) {
                toastify('No meter details available', 'error');
                return;
            }

            // Create current year-month in YYYY-MM format
            const now = new Date();
            const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

            // Get the current meter reading
            const meterReading = meterDetails.currentReading;

            // Check that we have all required data
            if (!meterDetails.meterNumber || !meterReading) {
                toastify('Missing meter number or reading', 'error');
                return;
            }

            setLoading(true);

            // Create payload according to backend requirements
            const payload = {
                meterNumber: meterDetails.meterNumber,
                yearMonth: yearMonth,
                meterReading: meterReading
            };

            // Make API call to generate bill
            const response = await makeRequest2(
                `${analog_base_url}/meter/billing/${selectedFacilityId}`,
                'POST',
                payload
            );

            if (response.success) {
                toastify('Bill and invoice generated successfully', 'success');
                // navigate(`/facility/invoices/view/${response.invoice._id}`);
            } else {
                toastify(response.error || 'Failed to generate bill', 'error');
            }
        } catch (error) {
            console.error('Error generating bill:', error);
            toastify('Error generating bill', 'error');
        } finally {
            setLoading(false);
        }
    };

    //function to handle opening the edit modal
    const handleEditAccount = (account) => {
        setSelectedAccountForEdit(account);
        setEditAccountForm({
            customerName: account.customerName || '',
            phoneNumber: account.phoneNumber || '',
            email: account.email || ''
        });
        setShowEditAccountModal(true);
    };

    // function to handle submitting the edit form
    const handleEditAccountSubmit = async () => {
        if (!selectedAccountForEdit) {
            toastify('No account selected for editing', 'error');
            return;
        }

        try {
            setLoading(true);

            const response = await makeRequest2(
                `${customer_account_base_url}/edit/${selectedFacilityId}/${meterId}/${selectedAccountForEdit.account_no}`,
                'PUT',
                editAccountForm
            );

            if (response.success) {
                toastify('Account updated successfully', 'success');
                setShowEditAccountModal(false);
                setSelectedAccountForEdit(null);
                setEditAccountForm({
                    customerName: '',
                    phoneNumber: '',
                    email: ''
                });

                // Refresh customer accounts
                fetchCustomerAccountsByMeterNumber(meterDetails.meterNumber);
            } else {
                toastify(response.error || 'Failed to update account', 'error');
            }
        } catch (error) {
            console.error('Error updating account:', error);
            toastify('Error updating account', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Stub functions for customer actions (to be implemented)
    const handleAssignCustomer = async () => {
        if (!selectedCustomerForAssignment) {
            toastify('Please select a customer', 'error');
            return;
        }

        try {
            // Find the selected customer
            const selectedCustomer = availableCustomers.find(
                customer => customer._id === selectedCustomerForAssignment
            );

            if (!selectedCustomer) {
                toastify('Customer not found', 'error');
                return;
            }

            // Prepare account data
            const accountData = {
                facilityId: selectedFacilityId,
                account_no: meterDetails.accountNumber,
                customerId: selectedCustomer._id,
                customerName: selectedCustomer.fullName ||
                    `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim(),
                phoneNumber: selectedCustomer.phoneNumber || 'N/A',
                email: selectedCustomer.email || 'N/A',
                meterNumber: meterDetails.meterNumber,
                unitId: meterDetails.unitId,
                payment_type: "Postpaid",
                previousReading: meterDetails.previousReading || 0,
                currentReading: meterDetails.currentReading || 0,
                meter_id: meterDetails._id,
                status: 'Active'
            };

            // Create customer account
            const accountResponse = await makeRequest2(
                `${customer_account_base_url}/add`,
                'POST',
                accountData
            );

            if (accountResponse.success) {
                // Update meter with the new customer ID
                const meterUpdateResponse = await makeRequest2(
                    `${analog_base_url}/${selectedFacilityId}/${meterId}`,
                    'PUT',
                    {
                        customerId: selectedCustomer._id,
                        customerName: accountData.customerName
                    }
                );

                if (meterUpdateResponse.success) {
                    toastify('Customer assigned successfully', 'success');

                    // Refresh customer accounts
                    fetchCustomerAccountsByMeterNumber(meterDetails.meterNumber);

                    // Close the modal
                    setShowAssignCustomerModal(false);
                    setSelectedCustomerForAssignment('');

                    // Refetch meter details to ensure all data is up to date
                    fetchMeterDetails();
                } else {
                    toastify('Customer account created, but failed to update meter', 'warning');
                }
            } else {
                toastify(accountResponse.error || 'Failed to assign customer', 'error');
            }
        } catch (error) {
            console.error('Error assigning customer:', error);
            toastify('Error assigning customer', 'error');
        }
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.setFontSize(18);
        doc.text('Water Meter Daily Reading History', 14, 22);

        doc.setFontSize(10);
        doc.text(`Meter Number: ${meterDetails?.meterNumber || 'N/A'}`, 14, 30);
        doc.text(`Unit: ${meterDetails?.UnitInfo?.name || 'N/A'}`, 14, 36);
        doc.text(`Current Reading: ${meterDetails?.currentReading?.toFixed(2) || '0.00'} m³`, 14, 42);
        doc.text(`Date of Export: ${new Date().toLocaleDateString()}`, 14, 48);

        const tableColumn = ["#", "Date", "Reading (m³)", "Status"];
        const tableRows = readings.map((reading, index) => [
            reading.date,
            reading.reading,
            reading.status
        ]);

        doc.autoTable({
            startY: 56,
            head: [tableColumn],
            body: tableRows,
            theme: 'striped',
            styles: { fontSize: 8 },
            headStyles: { fillColor: [40, 80, 120] }
        });

        doc.save(`Daily_Water_Meter_Readings_${meterDetails?.meterNumber}_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const exportToCSV = () => {
        const csvHeader = "No,Year-Month,Consumption (m³),Previous Reading (m³),Current Reading (m³),Reading Date\n";
        const csvRows = monthlyConsumption.map((item, index) =>
            `${item.yearMonth},${item.consumption},${item.previousReading},${item.currentReading},${item.readingDate}`
        ).join('\n');

        const csvContent = csvHeader + csvRows;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", `Daily_Water_Meter_Readings_${meterDetails?.meterNumber}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportHandler = (type) => {
        switch (type) {
            case 'pdf':
                exportToPDF();
                break;
            case 'csv':
                exportToCSV();
                break;
            default:
                toastify('Invalid export type', 'error');
        }
    };

    const handleActivateCustomer = async (account) => {
        try {
            // Ensure we have a facilityId
            if (!selectedFacilityId) {
                toastify('Facility ID is missing', 'error');
                return;
            }

            setLoading(true);
            const response = await makeRequest2(
                `${customer_account_base_url}/activate/${selectedFacilityId}/${meterId}`,
                'PUT',
                {
                    // account_no is shared by every account on this meter, so send the
                    // unique _id to activate exactly the row that was clicked
                    accountId: account._id,
                    account_no: account.account_no
                }
            );

            if (response.success) {
                toastify('Customer account activated successfully', 'success');

                // Refresh customer accounts
                fetchCustomerAccountsByMeterNumber(meterDetails.meterNumber);

                // Refetch meter details to ensure all data is up to date
                fetchMeterDetails();
            } else {
                toastify(response.error || 'Failed to activate account', 'error');
            }
        } catch (error) {
            console.error('Error activating customer:', error);
            toastify('Error activating customer', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeactivateCustomer = async () => {
        if (!customer || customerAccounts.length === 0) {
            toastify('No customer account to deactivate', 'error');
            return;
        }

        try {
            // Find the active account to deactivate
            const activeAccount = customerAccounts.find(acc => acc.status === 'Active');

            if (!activeAccount) {
                toastify('No active account found', 'error');
                return;
            }

            // Deactivate the customer account
            const accountDeactivationResponse = await makeRequest2(
                `${customer_account_base_url}/deactivate/${selectedFacilityId}/${meterId}`,
                'PUT',
                {
                    accountId: activeAccount._id,
                    account_no: activeAccount.account_no,
                    status: 'Inactive'
                }
            );

            if (accountDeactivationResponse.success) {
                // Update meter to remove customer details
                const meterUpdateResponse = await makeRequest2(
                    `${analog_base_url}/${selectedFacilityId}/${meterId}`,
                    'PUT',
                    {
                        customerId: null,
                        customerName: null
                    }
                );

                if (meterUpdateResponse.success) {
                    toastify('Customer account deactivated successfully', 'success');

                    // Refresh customer accounts
                    fetchCustomerAccountsByMeterNumber(meterDetails.meterNumber);

                    // Refetch meter details to ensure all data is up to date
                    fetchMeterDetails();
                } else {
                    toastify('Account deactivated, but failed to update meter', 'warning');
                }
            } else {
                toastify(accountDeactivationResponse.error || 'Failed to deactivate account', 'error');
            }
        } catch (error) {
            console.error('Error deactivating customer:', error);
            toastify('Error deactivating customer', 'error');
        }
    };

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb mb-3">
                                <li className="breadcrumb-item">
                                    <Link to="/facility">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="/facility/utility_management/meter_management/water_meters">Analog Meters Management</Link>
                                </li>
                                <li className="breadcrumb-item active">
                                    <span>Water Meters</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <div>
                                <Link
                                    to="/facility/utility_management/meter_management/water_meters"
                                    state={{ activeTab: "analog-meters" }}
                                >
                                    <i className="ti ti-arrow-narrow-left"></i> Back
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions Card */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="mb-0"><i className="ti ti-tool me-2"></i>Quick Actions</h5>
                        </div>
                        <div className="card-body">
                            <div className="d-flex flex-wrap gap-2">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setShowAddReadingModal(true)}
                                >
                                    <i className="ti ti-plus me-1"></i> Record New Reading
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={handleGenerateBill}
                                >
                                    <i className="ti ti-receipt me-1"></i> Generate Bill
                                </button>
                                <button
                                    className="btn btn-info"
                                    onClick={() => setShowEditModal(true)}
                                >
                                    <i className="ti ti-pencil me-1"></i> Edit
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={handleToggleMeterStatus}
                                >
                                    <i className="ti ti-trash me-1"></i> {meterDetails?.status === 'closed' ? 'Open Meter' : 'Close Meter'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Horizontal Tabs */}
                    <div className="card">
                        <div className="card-body py-0">
                            <ul className="nav nav-tabs profile-tabs">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeSection === 'meter-info' ? 'active' : ''}`}
                                        onClick={() => setActiveSection('meter-info')}
                                        type="button"
                                    >
                                        <i className="ti ti-dashboard me-2"></i>Meter Info
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeSection === 'customer-info' ? 'active' : ''}`}
                                        onClick={() => setActiveSection('customer-info')}
                                        type="button"
                                    >
                                        <i className="ti ti-users me-2"></i>Customer Info
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeSection === 'readings' ? 'active' : ''}`}
                                        onClick={() => setActiveSection('readings')}
                                        type="button"
                                    >
                                        <i className="ti ti-list-numbers me-2"></i>Reading History
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeSection === 'transactions' ? 'active' : ''}`}
                                        onClick={() => setActiveSection('transactions')}
                                        type="button"
                                    >
                                        <i className="ti ti-cash me-2"></i>Transactions
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeSection === 'consumption' ? 'active' : ''}`}
                                        onClick={() => setActiveSection('consumption')}
                                        type="button"
                                    >
                                        <i className="ti ti-list-numbers me-2"></i>Consumption History
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeSection === 'invoices' ? 'active' : ''}`}
                                        onClick={() => {
                                            setActiveSection('invoices');
                                            if (meterDetails?.meterNumber && meterDetails?.customerId) {
                                                fetchInvoices();
                                            }
                                        }}
                                        type="button"
                                    >
                                        <i className="ti ti-file-invoice me-2"></i>Invoices
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {loading ? (
                        <div className="card">
                            <div className="card-body text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading meter information...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="tab-content mt-4">
                            {/* Meter Information Section */}
                            {activeSection === 'meter-info' && (
                                <div className="card">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <table className="table">
                                                    <tbody>
                                                        <tr>
                                                            <td className="fw-bold">Serial Number:</td>
                                                            <td>{meterDetails?.meterNumber || 'N/A'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="fw-bold">Account Number:</td>
                                                            <td>{meterDetails?.accountNumber || 'N/A'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="fw-bold">Installation Date:</td>
                                                            <td>{meterDetails?.createdAt
                                                                ? new Date(meterDetails.createdAt).toLocaleDateString()
                                                                : 'N/A'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="fw-bold">Status:</td>
                                                            <td>
                                                                <span className={`badge ${meterDetails?.status === 'opened' ? 'bg-success text-light' :
                                                                    meterDetails?.status === 'maintenance' ? 'bg-warning text-light' :
                                                                        'bg-danger text-light'}`}>
                                                                    {meterDetails?.status || 'N/A'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="col-md-6">
                                                <table className="table">
                                                    <tbody>
                                                        <tr>
                                                            <td className="fw-bold">Unit:</td>
                                                            <td>{meterDetails?.UnitInfo
                                                                ? (meterDetails.UnitInfo.name || meterDetails.UnitInfo.unitNumber)
                                                                : 'N/A'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="fw-bold">Initial Reading:</td>
                                                            <td>{meterDetails?.initialReading?.toFixed(2) || '0.00'} m³</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="fw-bold">Previous Reading:</td>
                                                            <td>{meterDetails?.previousReading?.toFixed(2) || '0.00'} m³</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="fw-bold">Current Reading:</td>
                                                            <td>{meterDetails?.currentReading?.toFixed(2) || '0.00'} m³ (
                                                                {meterDetails?.lastReadingDate
                                                                    ? new Date(meterDetails.lastReadingDate).toLocaleDateString()
                                                                    : 'N/A'})
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Customer Information Section */}
                            {activeSection === 'customer-info' && (
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">Customer Information</h5>
                                        <div>
                                            <button
                                                className="btn btn-primary me-2"
                                                onClick={fetchAvailableCustomers}
                                            >
                                                Assign Account
                                            </button>
                                            <button
                                                className="btn btn-danger"
                                                onClick={handleDeactivateCustomer}
                                                disabled={!customer || customerAccounts.length === 0}
                                            >
                                                Deactivate Account
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        {customerAccounts && customerAccounts.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Customer Name</th>
                                                            <th>Phone Number</th>
                                                            <th>Unit</th>
                                                            <th>Payment Type</th>
                                                            <th>Status</th>
                                                            <th>Last Updated</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {customerAccounts.map((account, index) => (
                                                            <tr key={account._id || index} className={account.status === 'Active' ? 'table-active' : ''}>
                                                                <td>{account.customerName || 'N/A'}</td>
                                                                <td>{account.phoneNumber || 'N/A'}</td>
                                                                <td>{meterDetails?.UnitInfo
                                                                    ? (meterDetails.UnitInfo.name || meterDetails.UnitInfo.unitNumber)
                                                                    : 'N/A'}</td>
                                                                <td>{account.payment_type || 'N/A'}</td>
                                                                <td>
                                                                    <span className={`badge ${account.status === 'Active' ? 'bg-success text-light' :
                                                                        account.status === 'Inactive' ? 'bg-danger text-light' : 'bg-warning text-light'}`}>
                                                                        {account.status || 'N/A'}
                                                                    </span>
                                                                </td>
                                                                <td>{account.updatedAt ? new Date(account.updatedAt).toLocaleDateString() :
                                                                    account.created_on ? new Date(account.created_on).toLocaleDateString() : 'N/A'}</td>
                                                                <td>
                                                                    {account.status !== 'Active' && (
                                                                        <button
                                                                            className="btn btn-sm btn-success me-2"
                                                                            onClick={() => handleActivateCustomer(account)}
                                                                        >
                                                                            Activate
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        className="btn btn-sm btn-primary"
                                                                        onClick={() => handleEditAccount(account)}
                                                                        title="Edit Account"
                                                                    >
                                                                        <i className="ti ti-pencil"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4">
                                                <p>No customer accounts assigned to this meter.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Reading History Section */}
                            {activeSection === 'readings' && (
                                <div className="card">
                                    <div className="card-body">
                                        <div className="row mb-3">
                                            <div className="col-md-4">
                                                <div className="input-group">
                                                    <span className="input-group-text">Date</span>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={selectedDate}
                                                        onChange={(e) => handleDateChange(e.target.value)}
                                                    />
                                                    <button
                                                        className="btn btn-primary"
                                                        type="button"
                                                        onClick={handleFilter}
                                                    >
                                                        Filter
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="col-md-8 text-end">
                                                <div className="btn-group" role="group">
                                                    <button
                                                        className="btn btn-outline-success"
                                                        onClick={() => exportHandler('pdf')}
                                                    >
                                                        <i className="ti ti-file-type-pdf me-2"></i>Export PDF
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-primary"
                                                        onClick={() => exportHandler('csv')}
                                                    >
                                                        <i className="ti ti-file-type-csv me-2"></i>Export CSV
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Reading (m³)</th>
                                                        <th>Status</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {readings.length > 0 ? (
                                                        readings.map((reading, index) => (
                                                            <tr key={reading.id || index}>
                                                                <td>{reading.date}</td>
                                                                <td>{reading.reading}</td>
                                                                <td>{reading.status}</td>
                                                                <td>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        // onClick={() => fetchReadingImage(reading.id)}
                                                                        // disabled={loadingImage}
                                                                        title="View Reading Image"
                                                                    >

                                                                        <i className="ti ti-eye"></i>

                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center">No reading history found</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Edit Account Modal */}
                            {showEditAccountModal && selectedAccountForEdit && (
                                <div className="modal show d-block" tabIndex="-1">
                                    <div className="modal-dialog">
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h5 className="modal-title">Edit Account Details</h5>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    onClick={() => {
                                                        setShowEditAccountModal(false);
                                                        setSelectedAccountForEdit(null);
                                                        setEditAccountForm({
                                                            customerName: '',
                                                            phoneNumber: '',
                                                            email: ''
                                                        });
                                                    }}
                                                ></button>
                                            </div>
                                            <div className="modal-body">
                                                <form>
                                                    <div className="mb-3">
                                                        <label className="form-label">Account Number</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={selectedAccountForEdit.account_no}
                                                            disabled
                                                        />
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="form-label">Customer Name <span className="text-danger">*</span></label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editAccountForm.customerName}
                                                            onChange={(e) => setEditAccountForm({
                                                                ...editAccountForm,
                                                                customerName: e.target.value
                                                            })}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                                                        <input
                                                            type="tel"
                                                            className="form-control"
                                                            value={editAccountForm.phoneNumber}
                                                            onChange={(e) => setEditAccountForm({
                                                                ...editAccountForm,
                                                                phoneNumber: e.target.value
                                                            })}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="form-label">Email <span className="text-danger">*</span></label>
                                                        <input
                                                            type="email"
                                                            className="form-control"
                                                            value={editAccountForm.email}
                                                            onChange={(e) => setEditAccountForm({
                                                                ...editAccountForm,
                                                                email: e.target.value
                                                            })}
                                                            required
                                                        />
                                                    </div>
                                                </form>
                                            </div>
                                            <div className="modal-footer">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={() => {
                                                        setShowEditAccountModal(false);
                                                        setSelectedAccountForEdit(null);
                                                        setEditAccountForm({
                                                            customerName: '',
                                                            phoneNumber: '',
                                                            email: ''
                                                        });
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={handleEditAccountSubmit}
                                                    disabled={!editAccountForm.customerName || !editAccountForm.phoneNumber || !editAccountForm.email}
                                                >
                                                    Update Account
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Transactions Section */}
                            {activeSection === 'transactions' && (
                                <div className="card">
                                    <div className="card-body">
                                        {transactions.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Transaction ID</th>
                                                            <th>Date</th>
                                                            <th>Amount (KES)</th>
                                                            <th>Type</th>
                                                            <th>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {transactions.map((transaction, index) => (
                                                            <tr key={transaction.id || index}>
                                                                <td>{transaction.id}</td>
                                                                <td>{transaction.date}</td>
                                                                <td>{transaction.amount}</td>
                                                                <td>{transaction.type}</td>
                                                                <td>
                                                                    <span className={`badge ${transaction.status === 'Completed' ? 'bg-success text-light' : 'bg-warning text-light'}`}>
                                                                        {transaction.status}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4">
                                                <p>No transaction records found for this meter.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Assign Customer Modal */}
                            {showAssignCustomerModal && (
                                <div className="modal show d-block" tabIndex="-1">
                                    <div className="modal-dialog">
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h5 className="modal-title">Assign Customer to Meter</h5>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    onClick={() => {
                                                        setShowAssignCustomerModal(false);
                                                        setSelectedCustomerForAssignment('');
                                                    }}
                                                ></button>
                                            </div>
                                            <div className="modal-body">
                                                <div className="mb-3">
                                                    <label className="form-label">Select Customer</label>
                                                    <select
                                                        className="form-control"
                                                        value={selectedCustomerForAssignment}
                                                        onChange={(e) => setSelectedCustomerForAssignment(e.target.value)}
                                                    >
                                                        <option value="">Choose a customer</option>
                                                        {availableCustomers.map((customer) => (
                                                            <option key={customer._id} value={customer._id}>
                                                                {customer.fullName ||
                                                                    `${customer.firstName || ''} ${customer.lastName || ''}`.trim()}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="modal-footer">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={() => {
                                                        setShowAssignCustomerModal(false);
                                                        setSelectedCustomerForAssignment('');
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={handleAssignCustomer}
                                                    disabled={!selectedCustomerForAssignment}
                                                >
                                                    Assign Customer
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Water Invoices Section */}
                            {activeSection === 'invoices' && (
                                <div className="card">
                                    <div className="card-body">
                                        {invoices.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Inv Num</th>
                                                            <th>Date Issued</th>
                                                            <th>Period</th>
                                                            <th>Usage (m³)</th>
                                                            <th>Amount (KES)</th>
                                                            <th>Paid (KES)</th>
                                                            <th>Balance (KES)</th>
                                                            <th>Status</th>
                                                            <th>Due Date</th>
                                                            <th>Notifications</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {invoices.map((invoice, index) => (
                                                            <tr key={invoice._id || index}>
                                                                <td>{invoice.invoiceNumber || 'N/A'}</td>
                                                                <td>
                                                                    {invoice.dateIssued
                                                                        ? new Date(invoice.dateIssued).toLocaleDateString()
                                                                        : 'N/A'}
                                                                </td>
                                                                <td>{invoice.yearMonth || 'N/A'}</td>
                                                                <td>{invoice.meterReadings?.usage || '0'}</td>
                                                                <td>{invoice.charges?.totalMonthlyBill?.toFixed(2) || '0.00'}</td>
                                                                <td>{invoice.amountPaid?.toFixed(2) || '0.00'}</td>
                                                                <td>
                                                                    {((invoice.charges?.totalMonthlyBill || 0) - (invoice.amountPaid || 0)).toFixed(2)}
                                                                </td>
                                                                <td>
                                                                    <span className={`badge ${invoice.status === 'Paid' ? 'bg-success text-light' :
                                                                        invoice.status === 'Partially Paid' ? 'bg-warning text-light' :
                                                                            invoice.status === 'Pending' ? 'bg-secondary text-light' :
                                                                                'bg-danger text-light'
                                                                        }`}>
                                                                        {invoice.status || 'N/A'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {invoice.dueDate
                                                                        ? new Date(invoice.dueDate).toLocaleDateString()
                                                                        : 'N/A'}
                                                                </td>
                                                                <td>
                                                                    {/* Notification Status */}
                                                                    <div className="d-flex gap-1">
                                                                        {invoice.notificationsSent?.sms ? (
                                                                            <span
                                                                                className="badge bg-info text-light"
                                                                                title="SMS Sent"
                                                                            >
                                                                                <i className="ti ti-message"></i>
                                                                            </span>
                                                                        ) : (
                                                                            <span
                                                                                className="badge bg-secondary text-light"
                                                                                title="SMS Not Sent"
                                                                            >
                                                                                <i className="ti ti-message-off"></i>
                                                                            </span>
                                                                        )}
                                                                        {invoice.notificationsSent?.email ? (
                                                                            <span
                                                                                className="badge bg-primary text-light"
                                                                                title="Email Sent"
                                                                            >
                                                                                <i className="ti ti-mail"></i>
                                                                            </span>
                                                                        ) : (
                                                                            <span
                                                                                className="badge bg-secondary text-light"
                                                                                title="Email Not Sent"
                                                                            >
                                                                                <i className="ti ti-mail-off"></i>
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {/* View Action Button */}
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => handleViewInvoice(invoice)}
                                                                        title="View Invoice"
                                                                    >
                                                                        <i className="ti ti-eye"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4">
                                                <p>No invoices found for this meter.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Consumption History Section */}
                            {activeSection === 'consumption' && (
                                <div className="card">
                                    <div className="card-body">
                                        <div className="row mb-3">
                                            <div className="col-md-4">
                                                <div className="input-group">
                                                    <span className="input-group-text">Date</span>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={selectedDate}
                                                        onChange={(e) => handleDateChange(e.target.value)}
                                                    />
                                                    <button
                                                        className="btn btn-primary"
                                                        type="button"
                                                        onClick={handleFilter}
                                                    >
                                                        Filter
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="col-md-8 text-end">
                                                <div className="btn-group">
                                                    <button
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => toastify('Exporting CSV...', 'info')}
                                                    >
                                                        <i className="ti ti-file-export me-1"></i>Export CSV
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => toastify('Exporting PDF...', 'info')}
                                                    >
                                                        <i className="ti ti-file-export me-1"></i>Export PDF
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>Year - Month</th>
                                                        <th>Consumption (m³)</th>
                                                        <th>Initial Reading (m³)</th>
                                                        <th>Final Reading (m³)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {monthlyConsumption.length > 0 ? (
                                                        monthlyConsumption.map((item, index) => (
                                                            <tr key={item.id || index}>
                                                                <td>{item.yearMonth}</td>
                                                                <td>{item.consumption}</td>
                                                                <td>{item.initialReading}</td>
                                                                <td>{item.finalReading}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="6" className="text-center">No consumption history found</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Edit Meter Details Modal */}
            {showEditModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Meter Details</h5>
                                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    <div className="mb-3">
                                        <label className="form-label">Meter Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editForm.meterNumber}
                                            onChange={(e) => setEditForm({ ...editForm, meterNumber: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Account Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editForm.accountNumber}
                                            onChange={(e) => setEditForm({ ...editForm, accountNumber: e.target.value })}
                                            readOnly
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Unit<span style={{ color: "red" }}>*</span></label>
                                        <select
                                            className="form-control"
                                            value={editForm.unitId}
                                            onChange={(e) => setEditForm({ ...editForm, unitId: e.target.value })}
                                        >
                                            <option value="">Select Unit</option>
                                            {units.map((unit) => (
                                                <option key={unit._id} value={unit._id}>
                                                    {unit.unitNumber || unit.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Close</button>
                                <button type="button" className="btn btn-primary" onClick={handleEditSubmit}>Save changes</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add New Reading Modal */}
            {showAddReadingModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Record New Reading</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAddReadingModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    <div className="mb-3">
                                        <label className="form-label">Current Reading</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={newReading}
                                            onChange={(e) => setNewReading(e.target.value)}
                                        />
                                    </div>
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAddReadingModal(false)}>Close</button>
                                <button type="button" className="btn btn-primary" onClick={handleNewReadingSubmit}>Save Reading</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}

export default ViewAnalogWaterMeter;
